import React, { Fragment } from 'react';

import { useBasketContext } from '../../../../data/context/basketContext/basketContext';
import formatPrice from '../../../../helpers/formatPrice';
import { useSelectedQuantityContext, useSelectedVariantContext } from '../../context/TreatmentSelectorContext';
import useGetNewUserDiscount from './hooks/useGetNewUserDiscount';
import { getDiscountAmount } from './helpers/getDiscountAmount/getDiscountAmount';

import Tag from '../../../../components/_ui/_blocks/Tag/Tag';
import Typography from '../../../../components/_ui/_blocks/Typography/Typography';
import DiscountRow, { DiscountRowLoading } from './DiscountRow/DiscountRow';
import DeliveryRow from './DeliveryRow/DeliveryRow';
import QuantityRow from './QuantityRow/QuantityRow';

import styles from './TotalsSummary.module.css';

const amountRequiredForFreeDelivery = 4000;

const TotalsSummary = () => {
    // TODO: fix this
    // @ts-expect-error
    const { basket, minimumDeliveryCost } = useBasketContext();
    const selectedVariant = useSelectedVariantContext();
    const selectedQuantity = useSelectedQuantityContext();

    const price = selectedQuantity?.price || 0;
    const basketTotal = basket?.total || 0;

    const amountRemainingForFreeDelivery = amountRequiredForFreeDelivery - (basketTotal + price);

    // If they have free delivery, return the price. Otherwise, return price with delivery cost.
    const total = amountRemainingForFreeDelivery <= 0 ? price : (price + minimumDeliveryCost);

    const { isLoading, discount } = useGetNewUserDiscount(selectedVariant);
    const discountAmount = discount ? getDiscountAmount(discount, price) : 0;

    const discountedTotal = total - discountAmount;

    // If the total (or discount total, whichever is smaller) is less than 0, then the order is free.
    const isFree = Math.min(total, discountedTotal) <= 0;

    if (!price) {
        return (
            <ul className={styles.list}>
                <DeliveryRow
                    amountRemainingForFreeDelivery={amountRemainingForFreeDelivery}
                    minimumDeliveryCost={minimumDeliveryCost}
                />
            </ul>
        );
    }

    return (
        <ul className={styles.list}>
            {/* If there is only one quantity and it's selected. */}
            {selectedVariant.quantities.length === 1 && selectedQuantity ? (
                <QuantityRow selectedQuantity={selectedQuantity} />
            ) : null}
            <DeliveryRow
                amountRemainingForFreeDelivery={amountRemainingForFreeDelivery}
                minimumDeliveryCost={minimumDeliveryCost}
            />
            {isLoading ? (
                <DiscountRowLoading />
            ) : (
                <Fragment>
                    {discount ? <DiscountRow discountAmount={discountAmount} discount={discount} /> : null}
                </Fragment>
            )}
            <li>
                <Typography as="div" size="090" weight="600">Total</Typography>
                {isLoading ? (
                    <div className="skeleton-animation w-[6rem] h-[1.75rem]" />
                ) : (
                    <Fragment>
                        {isFree ? (
                            <Tag size="medium" color="info">Free</Tag>
                        ) : (
                            <div className="flex justify-between items-center gap-050">
                                {discount ? (
                                    <Typography as="div" color="special-offer" lineThrough>
                                        {formatPrice(total)}
                                    </Typography>
                                ) : null}
                                <Typography as="div" size="300" color="price" weight="700" lineHeight="300">
                                    {formatPrice(discount ? discountedTotal : total)}
                                </Typography>
                            </div>
                        )}
                    </Fragment>
                )}
            </li>
        </ul>
    );
};

export default TotalsSummary;
